const DEFAULT_BASE_URL = 'https://marketdata.jlbhk.com';
const DEFAULT_TIME_ZONE = 'Asia/Hong_Kong';
const DEFAULT_TIMEOUT_MS = 15_000;

export const marketDataConfig = {
  baseUrl: String(import.meta.env.VITE_MARKET_DATA_API_BASE_URL || DEFAULT_BASE_URL).replace(/\/+$/, ''),
  timeZone: String(import.meta.env.VITE_MARKET_DATA_TIME_ZONE || DEFAULT_TIME_ZONE),
  timeoutMs: Math.max(
    3_000,
    Number(import.meta.env.VITE_MARKET_DATA_API_TIMEOUT_MS) || DEFAULT_TIMEOUT_MS
  ),
  quoteRefreshMs: Math.max(
    30_000,
    Number(import.meta.env.VITE_MARKET_DATA_QUOTE_REFRESH_MS) || 60_000
  ),
  contentRefreshMs: Math.max(
    300_000,
    Number(import.meta.env.VITE_MARKET_DATA_CONTENT_REFRESH_MS) || 3_600_000
  ),
};


export const getMarketContentLanguage = (language?: string | null) => {
  const normalized = String(language || 'en').trim().toLowerCase().replace('_', '-');
  const base = normalized.split('-')[0];

  if (normalized === 'hk' || normalized.startsWith('zh-hk') || normalized.startsWith('zh-hant')) {
    return 'zh-HK';
  }
  if (normalized === 'cn' || normalized.startsWith('zh-cn') || normalized.startsWith('zh-hans')) {
    return 'zh-CN';
  }
  if (normalized === 'jp' || base === 'ja') return 'ja';
  if (normalized === 'kr' || base === 'ko') return 'ko';
  if (normalized === 'vn' || base === 'vi') return 'vi';
  if (base === 'th') return 'th';
  if (base === 'de') return 'de';
  if (base === 'fr') return 'fr';
  if (base === 'it') return 'it';
  if (base === 'es') return 'es';
  if (base === 'pt') return 'pt';
  if (base === 'ar') return 'ar';
  return 'en';
};

export type SparklinePoint = {
  time: string;
  timeLocal?: string | null;
  value: number | string;
};

export type MarketQuoteItem = {
  providerSymbol: string;
  displaySymbol: string;
  displayName: string;
  assetClass: 'FOREX' | 'COMMODITY' | 'INDEX' | 'STOCK' | string;
  exchange?: string | null;
  baseCurrency?: string | null;
  quoteCurrency?: string | null;
  priceScale: number;
  price?: number | string | null;
  change?: number | string | null;
  changePercentage?: number | string | null;
  sellingPrice?: number | string | null;
  buyingPrice?: number | string | null;
  pricingType?: string | null;
  dayLow?: number | string | null;
  dayHigh?: number | string | null;
  yearLow?: number | string | null;
  yearHigh?: number | string | null;
  providerTimestamp?: string | null;
  providerTimestampLocal?: string | null;
  fetchedAt?: string | null;
  fetchedAtLocal?: string | null;
  delayed?: boolean;
  stale?: boolean;
  sparkline24h?: SparklinePoint[];
};

export type MarketBoardResponse = {
  assetClass: string;
  generatedAt: string;
  generatedAtLocal?: string | null;
  timeZone: string;
  staleAfterSeconds: number;
  items: MarketQuoteItem[];
};

export type ChartPoint = {
  time: string;
  timeLocal?: string | null;
  open: number | string;
  high: number | string;
  low: number | string;
  close: number | string;
};

export type MarketChartResponse = {
  providerSymbol: string;
  displaySymbol: string;
  displayName: string;
  assetClass: string;
  range: string;
  interval: string;
  source: string;
  timeZone: string;
  from?: string | null;
  fromLocal?: string | null;
  to?: string | null;
  toLocal?: string | null;
  open?: number | string | null;
  high?: number | string | null;
  low?: number | string | null;
  close?: number | string | null;
  change?: number | string | null;
  changePercentage?: number | string | null;
  delayed?: boolean;
  points: ChartPoint[];
};

export type EconomicCalendarItem = {
  id: number;
  eventTimeUtc: string;
  eventTime: string;
  countryCode?: string | null;
  currency?: string | null;
  event: string;
  previous?: number | string | null;
  estimate?: number | string | null;
  actual?: number | string | null;
  change?: number | string | null;
  changePercentage?: number | string | null;
  impact?: string | null;
  importance?: number | null;
  unit?: string | null;
  fetchedAt?: string | null;
  fetchedAtLocal?: string | null;
};

export type EconomicCalendarResponse = {
  timeZone: string;
  from: string;
  toExclusive: string;
  count: number;
  items: EconomicCalendarItem[];
};

export type MarketNewsItem = {
  id: number;
  type: string;
  publishedAtUtc: string;
  publishedAt: string;
  title: string;
  snippet?: string | null;
  site?: string | null;
  url?: string | null;
  imageUrl?: string | null;
  symbols?: string | null;
  fetchedAt?: string | null;
  fetchedAtLocal?: string | null;
};

export type MarketNewsResponse = {
  timeZone: string;
  page: number;
  size: number;
  total: number;
  items: MarketNewsItem[];
};

export class MarketDataApiError extends Error {
  status: number;

  constructor(message: string, status = 0) {
    super(message);
    this.name = 'MarketDataApiError';
    this.status = status;
  }
}

const createUrl = (path: string, params: Record<string, string | number | boolean | undefined>) => {
  const url = new URL(`${marketDataConfig.baseUrl}${path}`);
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === '') return;
    url.searchParams.set(key, String(value));
  });
  return url.toString();
};

const getJson = async <T>(
  path: string,
  params: Record<string, string | number | boolean | undefined>,
  signal?: AbortSignal
): Promise<T> => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), marketDataConfig.timeoutMs);

  const abortFromCaller = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    signal.addEventListener('abort', abortFromCaller, { once: true });
  }

  try {
    const response = await fetch(createUrl(path, params), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      signal: controller.signal,
    });

    if (!response.ok) {
      let message = `Market information is temporarily unavailable (HTTP ${response.status}).`;
      try {
        const payload = (await response.json()) as { message?: string };
        if (payload?.message) message = payload.message;
      } catch {
        // Keep the HTTP fallback message when the body is not JSON.
      }
      throw new MarketDataApiError(message, response.status);
    }

    return (await response.json()) as T;
  } catch (error) {
    if (error instanceof MarketDataApiError) throw error;
    if (error instanceof DOMException && error.name === 'AbortError') {
      if (signal?.aborted) throw error;
      throw new MarketDataApiError('The market data service did not respond in time.');
    }
    throw new MarketDataApiError(
      'Unable to load the latest market information. Please try again shortly.'
    );
  } finally {
    window.clearTimeout(timeoutId);
    signal?.removeEventListener('abort', abortFromCaller);
  }
};

export const fetchMarketBoard = (
  assetClass: 'FOREX' | 'COMMODITY' | 'INDEX' | 'STOCK',
  signal?: AbortSignal
) =>
  getJson<MarketBoardResponse>(
    '/api/v1/market/board',
    {
      assetClass,
      includeSparkline: true,
      timeZone: marketDataConfig.timeZone,
    },
    signal
  );

export const fetchMarketChart = (
  symbol: string,
  range: '24H' | '7D' | '1M' | '3M' | '1Y' | '5Y',
  signal?: AbortSignal
) =>
  getJson<MarketChartResponse>(
    '/api/v1/market/chart',
    { symbol, range, timeZone: marketDataConfig.timeZone },
    signal
  );

export type EconomicCalendarQueryOptions = {
  limit?: number;
  language?: string;
};

export const fetchEconomicCalendar = (
  from: string,
  to: string,
  signal?: AbortSignal,
  options: EconomicCalendarQueryOptions = {}
) =>
  getJson<EconomicCalendarResponse>(
    '/api/v1/economic-calendar',
    {
      from,
      to,
      timeZone: marketDataConfig.timeZone,
      limit: Math.min(500, Math.max(1, options.limit ?? 100)),
      language: getMarketContentLanguage(options.language),
    },
    signal
  );

export type MarketNewsQuery = {
  type?: 'ALL' | 'GENERAL' | 'FOREX' | 'STOCK' | string;
  page?: number;
  size?: number;
  q?: string;
  language?: string;
};

export const fetchMarketNews = (
  query: MarketNewsQuery = {},
  signal?: AbortSignal
) =>
  getJson<MarketNewsResponse>(
    '/api/v1/news',
    {
      type: query.type || 'ALL',
      page: query.page ?? 0,
      size: query.size ?? 20,
      q: query.q,
      language: getMarketContentLanguage(query.language),
      timeZone: marketDataConfig.timeZone,
    },
    signal
  );
